'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/app/providers';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  const t = useTranslations();

  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Application error:', error);
  }, [error]);

  return (
    <div className="min-h-screen hero-gradient flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-error-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-brand-violet-500/10 rounded-full blur-3xl" />
      </div>

      {/* Logo */}
      <Link href="/" className="mb-12 relative z-10">
        <Image
          src="/GeeksCreations Logo.svg"
          alt="GeeksCreations"
          width={120}
          height={54}
          className="h-14 w-auto"
          priority
        />
      </Link>

      {/* Error content */}
      <div className="relative z-10 text-center max-w-2xl">
        {/* Error icon with glitch effect */}
        <div className="relative mb-8 inline-block">
          <div className="text-[6rem] md:text-[8rem] select-none">
            <span className="relative inline-block">
              <span className="absolute inset-0 text-error-500/30 blur-sm animate-pulse">{'</>'}</span>
              <span className="relative bg-gradient-to-r from-error-400 via-brand-violet-400 to-error-400 bg-clip-text text-transparent">
                {'</>'}
              </span>
            </span>
          </div>
        </div>

        {/* Terminal-style error */}
        <div className="card-gradient-border rounded-2xl mb-10 mx-auto max-w-lg">
          <div className="p-6 rounded-2xl">
            <div className="font-mono text-left space-y-2 text-sm">
              <p className="text-error-400">
                <span className="text-slate-400">[ERROR]</span> {t.errors?.runtime?.type || 'RuntimeException'}
              </p>
              <p className="text-slate-300 break-all">
                <span className="text-slate-500">message:</span> {t.errors?.runtime?.message || 'Something went wrong'}
              </p>
              {error.digest && (
                <p className="text-slate-500 text-xs">
                  <span>digest:</span> {error.digest}
                </p>
              )}
              <p className="text-slate-400 animate-pulse mt-2">
                █
              </p>
            </div>
          </div>
        </div>

        <p className="text-lg md:text-xl text-slate-300 mb-10 leading-relaxed">
          {t.errors?.runtime?.description || "We encountered an unexpected error. Our team has been notified."}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button onClick={reset}>
            {t.errors?.runtime?.retryButton || 'Try Again'}
          </Button>
          <Button variant="outline-light" asChild>
            <Link href="/">
              {t.errors?.runtime?.homeButton || 'Back to Home'}
            </Link>
          </Button>
        </div>
      </div>

      {/* Decorative code lines */}
      <div className="absolute bottom-8 left-8 font-mono text-xs text-slate-600 hidden md:block">
        <p>{'// Oops! An exception occurred'}</p>
        <p>{'// catch (e) { handleGracefully() }'}</p>
      </div>

      {/* Debug info in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute bottom-8 right-8 font-mono text-xs text-slate-600 max-w-xs text-right hidden md:block">
          <p className="text-error-500/60 break-all">{error.message}</p>
        </div>
      )}
    </div>
  );
}

